/*
 * Image Scroll Carousel
 * ---------------------
 * Drives the pinned, scroll-jacked horizontal track in
 * sections/image-scroll-carousel.liquid. As the user scrolls the page
 * vertically, the slide track translates horizontally. The section wrapper is
 * given enough extra height to provide the scroll distance, and a sticky inner
 * pin keeps the content fixed on screen while it plays.
 *
 * On screens < 900px or when the user prefers reduced motion the scroll-jack is
 * disabled and the CSS falls back to a native horizontal swipe (scroll-snap).
 *
 * No dependencies. Self-initialises every [data-isc] element on the page and
 * re-wires itself for Shopify theme-editor events.
 */
(function () {
  'use strict';

  var MOBILE_BREAKPOINT = 900; // must match $isc-bp in the SCSS
  var PEEK = 0.1;              // fraction of the next slide left peeking in
  var MAX_FULL_SLIDES = 2;    // most full slides shown at once on desktop
  var MIN_SLIDE_W = 300;      // px — show one fewer full slide rather than shrink below this
  var SLIDE_RATIO = 320 / 600; // card width / height — must match the SCSS aspect-ratio
  var controllers = {}; // keyed by section id

  function prefersReducedMotion() {
    return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function clamp(value, min, max) {
    return Math.max(min, Math.min(max, value));
  }
  function ScrollCarousel(root) {
    this.root = root;
    this.viewport = root.querySelector('.isc__viewport');
    this.track = root.querySelector('.isc__track');
    this.maxTranslate = 0;
    this.ticking = false;
    this.enabled = false;

    this.onScroll = this.onScroll.bind(this);
    this.onResize = this.onResize.bind(this);
    this.render = this.render.bind(this);

    window.addEventListener('scroll', this.onScroll, { passive: true });
    window.addEventListener('resize', this.onResize, { passive: true });

    this.layout();
  }

  // Measure, decide whether the scroll-jack should run, and size the wrapper so
  // 1px of vertical page scroll maps to ~1px of horizontal track travel.
  ScrollCarousel.prototype.layout = function () {
    if (!this.viewport || !this.track) { return; }

    var isStatic = this.root.classList.contains('isc--static');
    var disabled = window.innerWidth < MOBILE_BREAKPOINT || prefersReducedMotion() || isStatic;

    if (disabled) {
      this.enabled = false;
      this.root.style.height = '';
      this.track.style.transform = '';
      this.root.style.removeProperty('--isc-slide-w');

      // Drag-to-scroll for the static desktop carousel (not needed on mobile —
      // touch devices get native swipe via overflow-x:auto).
      var wantDrag = isStatic && window.innerWidth >= MOBILE_BREAKPOINT && !prefersReducedMotion();
      if (wantDrag && !this._dragActive) {
        this._initDrag();
      } else if (!wantDrag && this._dragActive) {
        this._destroyDrag();
      }
      return;
    }

    // Switching back to scroll-jack mode — remove drag if it was active.
    if (this._dragActive) { this._destroyDrag(); }

    this.enabled = true;
    this.sizeSlides();
    this.maxTranslate = Math.max(0, this.track.scrollWidth - this.viewport.clientWidth);
    this.root.style.height = (window.innerHeight + this.maxTranslate) + 'px';
    this.render();
  };

  ScrollCarousel.prototype._initDrag = function () {
    var viewport = this.viewport;
    var startX = 0, startScrollLeft = 0, dragging = false, moved = false;

    function onMouseDown(e) {
      dragging = true;
      moved = false;
      startX = e.pageX;
      startScrollLeft = viewport.scrollLeft;
      viewport.classList.add('isc__viewport--grabbing');
    }

    function onMouseMove(e) {
      if (!dragging) { return; }
      var dx = e.pageX - startX;
      if (Math.abs(dx) > 4) { moved = true; }
      viewport.scrollLeft = startScrollLeft - dx;
    }

    function onMouseUp() {
      dragging = false;
      viewport.classList.remove('isc__viewport--grabbing');
    }

    // Capture-phase click suppression prevents link navigation after a drag.
    function onClick(e) {
      if (moved) {
        e.preventDefault();
        e.stopImmediatePropagation();
        moved = false;
      }
    }

    viewport.addEventListener('mousedown', onMouseDown);
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
    viewport.addEventListener('click', onClick, true);

    this._dragActive = true;
    this._destroyDrag = function () {
      viewport.removeEventListener('mousedown', onMouseDown);
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
      viewport.removeEventListener('click', onClick, true);
      viewport.classList.remove('isc__viewport--grabbing');
      this._dragActive = false;
    }.bind(this);
  };

  // Size each slide so MAX_FULL_SLIDES + a 10% peek fit the viewport width.
  // As the viewport narrows the cards would get steadily smaller, so once they
  // drop below MIN_SLIDE_W we show one fewer full slide instead (e.g. 2.1 -> 1.1)
  // — this keeps cards a comfortable size rather than shrinking indefinitely.
  // For a count of `full` slides + 0.1 peek there are exactly `full` gaps on
  // screen. Finally the card height is derived from its width via the SCSS
  // aspect-ratio, so we also cap the width to whatever keeps that height within
  // the viewport — otherwise the card overflows and the text panel is clipped.
  ScrollCarousel.prototype.sizeSlides = function () {
    var gap = parseFloat(window.getComputedStyle(this.track).columnGap) || 0;
    var avail = this.viewport.clientWidth;

    var full = MAX_FULL_SLIDES;
    var width = (avail - gap * full) / (full + PEEK);
    while (full > 1 && width < MIN_SLIDE_W) {
      full -= 1;
      width = (avail - gap * full) / (full + PEEK);
    }

    width = Math.min(width, this.viewport.clientHeight * SLIDE_RATIO);
    this.root.style.setProperty('--isc-slide-w', width + 'px');
  };

  ScrollCarousel.prototype.onScroll = function () {
    if (!this.enabled || this.ticking) { return; }
    this.ticking = true;
    window.requestAnimationFrame(this.render);
  };

  ScrollCarousel.prototype.onResize = function () {
    this.layout();
  };

  ScrollCarousel.prototype.render = function () {
    this.ticking = false;
    if (!this.enabled) { return; }

    var scrollable = this.root.offsetHeight - window.innerHeight;
    if (scrollable <= 0) { return; }

    var progress = clamp(-this.root.getBoundingClientRect().top / scrollable, 0, 1);
    this.track.style.transform = 'translate3d(' + (-progress * this.maxTranslate) + 'px, 0, 0)';
  };

  // Scroll the page so a given child block sits at the matching point of the
  // track travel — used when a merchant selects a slide in the theme editor.
  ScrollCarousel.prototype.scrollToBlock = function (blockEl) {
    if (!this.enabled || !blockEl) { return; }
    var slideLeft = blockEl.offsetLeft;
    var ratio = this.maxTranslate > 0 ? clamp(slideLeft / this.maxTranslate, 0, 1) : 0;
    var scrollable = this.root.offsetHeight - window.innerHeight;
    var target = this.root.offsetTop + (ratio * scrollable);
    window.scrollTo({ top: target, behavior: 'smooth' });
  };

  ScrollCarousel.prototype.destroy = function () {
    window.removeEventListener('scroll', this.onScroll);
    window.removeEventListener('resize', this.onResize);
    if (this._dragActive) { this._destroyDrag(); }
    this.root.style.height = '';
    if (this.track) { this.track.style.transform = ''; }
  };

  function init(root) {
    if (!root || root.hasAttribute('data-isc-ready')) { return; }
    root.setAttribute('data-isc-ready', 'true');
    markTruncatedSlides(root);
    var controller = new ScrollCarousel(root);
    var id = root.getAttribute('data-section-id');
    if (id) { controllers[id] = controller; }
  }

  function initAll() {
    var nodes = document.querySelectorAll('[data-isc]');
    for (var i = 0; i < nodes.length; i++) { init(nodes[i]); }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  // Recompute once everything (images/fonts) has loaded and track width settles.
  window.addEventListener('load', function () {
    for (var id in controllers) {
      if (controllers.hasOwnProperty(id)) { controllers[id].layout(); }
    }
    document.querySelectorAll('[data-isc]').forEach(markTruncatedSlides);
  });

  // Text reflow on viewport width changes can change whether a slide's copy
  // is actually truncated, so re-check the read-more labels on resize too.
  window.addEventListener('resize', function () {
    document.querySelectorAll('[data-isc]').forEach(markTruncatedSlides);
  }, { passive: true });

  // Shopify theme editor support.
  document.addEventListener('shopify:section:load', function (e) {
    var root = e.target && e.target.querySelector ? e.target.querySelector('[data-isc]') : null;
    if (root) { init(root); }
  });

  document.addEventListener('shopify:section:unload', function (e) {
    var id = e.detail && e.detail.sectionId;
    if (id && controllers[id]) {
      controllers[id].destroy();
      delete controllers[id];
    }
  });

  document.addEventListener('shopify:block:select', function (e) {
    var id = e.detail && e.detail.sectionId;
    if (id && controllers[id]) { controllers[id].scrollToBlock(e.target); }
  });
})();
